import { computed, inject, Injectable, signal } from '@angular/core';
import { AppError } from '@/app/core/http/app-error.model';
import { toAppError } from '@/app/core/http/error-catalog';
import { Settlement, SettlementUpload } from '../models/settlement.model';
import { SettlementRepository } from '../services/settlement.repository';

export type SettlementCreateStatus = 'idle' | 'submitting' | 'created' | 'error';

const ERROR_SUBJECT = 'la carga de bonos';

const selectionKey = (upload: SettlementUpload): string =>
  [upload.mode, upload.mode === 'FILE' ? upload.fileName : '', ...[...upload.folios].sort()].join(
    ',',
  );

@Injectable()
export class SettlementCreateStore {
  private readonly repository = inject(SettlementRepository);

  private readonly _status = signal<SettlementCreateStatus>('idle');
  private readonly _settlement = signal<Settlement | null>(null);
  private readonly _error = signal<AppError | null>(null);

  private idempotency: { selection: string; key: string } | null = null;

  readonly status = this._status.asReadonly();
  readonly settlement = this._settlement.asReadonly();
  readonly error = this._error.asReadonly();
  readonly isSubmitting = computed(() => this._status() === 'submitting');

  // El store vive en la ruta: sin esto, al volver a la página se repetiría el último resultado.
  reset(): void {
    this.idempotency = null;
    this._settlement.set(null);
    this._error.set(null);
    this._status.set('idle');
  }

  create(upload: SettlementUpload): void {
    if (!upload.folios.length || this.isSubmitting()) return;

    this._settlement.set(null);
    this._error.set(null);
    this._status.set('submitting');

    this.repository.create(upload, this.idempotencyKeyFor(upload)).subscribe({
      next: (settlement) => {
        this.idempotency = null;
        this._settlement.set(settlement);
        this._status.set('created');
      },
      error: (cause: unknown) => {
        this._error.set(toAppError(cause, ERROR_SUBJECT));
        this._status.set('error');
      },
    });
  }

  private idempotencyKeyFor(upload: SettlementUpload): string {
    const selection = selectionKey(upload);
    if (this.idempotency?.selection !== selection) {
      this.idempotency = { selection, key: crypto.randomUUID() };
    }
    return this.idempotency.key;
  }
}
